import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useI18n } from '../../../i18n/index.jsx';
import { nodeName } from '../../../categoryTree.js';
import CategoryTile, { CategoryPhoto } from '../../../components/CategoryTile.jsx';

// Step 1: what the seller sells. Type to search the whole tree (results
// show the full path), or open the photo tiles level by level. Products go
// under a product type (level 4), or a sub-category (level 3) that has
// none. The seller's recent categories come first.
const levelOf = (tree, n) => tree.path(n.id).length;
// Levels 1–3 open; nothing below level 4 is offered.
const kidsOf = (tree, n) => (levelOf(tree, n) >= 4 ? [] : tree.children(n.id));
export const canPick = (tree, n) => !!n && (levelOf(tree, n) === 4 || (levelOf(tree, n) === 3 && !tree.children(n.id).length));
export default function CategoryStep({ tree, value, recent, onPick, error }) {
  const { t, lang } = useI18n();
  const [at, setAt] = useState(() => (value ? tree.get(value)?.parentId ?? null : null));
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const searchRef = useRef(null);
  // The tree arrives after the first render on a fresh page load.
  const loaded = tree.list.length > 0;
  useEffect(() => { if (loaded && value) setAt(tree.get(value)?.parentId ?? null); }, [loaded]); // eslint-disable-line react-hooks/exhaustive-deps

  const hits = useMemo(() => (q.trim().length >= 2 ? tree.search(q, lang, 60).filter((h) => canPick(tree, h.node)).slice(0, 12) : []), [q, tree, lang]);
  useEffect(() => { setActive(0); }, [q]);
  const crumbs = at ? tree.path(at) : [];
  const current = at ? tree.get(at) : null;
  const level = at ? kidsOf(tree, current) : tree.children(null);
  const recentNodes = (recent || []).map((id) => tree.get(id)).filter((n) => canPick(tree, n)).slice(0, 5);
  const chosen = value ? tree.get(value) : null;

  const open = (n) => {
    if (kidsOf(tree, n).length) { setAt(n.id); window.scrollTo({ top: 0, behavior: 'smooth' }); } else if (canPick(tree, n)) onPick(n.id);
    else setAt(n.id); // nothing below, not a product type: "Put my product here anyway"
  };
  const pickHit = (n) => { setQ(''); setAt(n.parentId ?? null); onPick(n.id); };
  const onKey = (e) => {
    if (!hits.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(hits.length - 1, i + 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(0, i - 1)); }
    if (e.key === 'Enter') { e.preventDefault(); pickHit(hits[active].node); }
  };

  if (!loaded) return <p className="muted">{t('common.loading')}</p>;

  return (
    <div className="wz-cat">
      {chosen && (
        <div className="wz-chosen">
          <CategoryPhoto tree={tree} node={chosen} lang={lang} className="cat-photo cat-photo-sm" />
          <div><span className="muted small">{t('wizard.chosenCategory')}</span><strong>{tree.pathText(chosen.id, lang)}</strong></div>
        </div>
      )}

      <div className="wz-search">
        <label htmlFor="wz-q" className="field-label">{t('wizard.searchLabel')}</label>
        <input id="wz-q" ref={searchRef} type="search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKey} autoComplete="off"
          placeholder={t('wizard.searchPlaceholder')} aria-controls="wz-results" aria-invalid={!!error || undefined} />
        {q.trim().length >= 2 && (
          <ul id="wz-results" className="wz-results" aria-label={t('wizard.results')} aria-live="polite">
            {hits.map((h, i) => (
              <li key={h.node.id}>
                <button type="button" className={i === active ? 'on' : ''} onMouseEnter={() => setActive(i)} onClick={() => pickHit(h.node)}>
                  <CategoryPhoto tree={tree} node={h.node} lang={lang} className="cat-photo cat-photo-xs" />
                  <span>{h.path.map((n, j) => <React.Fragment key={n.id}>{j > 0 && <i aria-hidden="true"> › </i>}{j === h.path.length - 1 ? <strong>{nodeName(n, lang)}</strong> : nodeName(n, lang)}</React.Fragment>)}</span>
                </button>
              </li>
            ))}
            {!hits.length && <li className="wz-noresult muted">{t('wizard.noMatch')}</li>}
          </ul>
        )}
      </div>

      {recentNodes.length > 0 && !q && (
        <div className="wz-recent">
          <span className="field-label">{t('wizard.recent')}</span>
          <div className="chip-choice">
            {recentNodes.map((n) => (
              <button key={n.id} type="button" className={`chip-toggle${n.id === Number(value) ? ' on' : ''}`} onClick={() => pickHit(n)}>{tree.pathText(n.id, lang)}</button>
            ))}
          </div>
        </div>
      )}

      <nav className="wz-crumbs" aria-label={t('wizard.breadcrumb')}>
        <button type="button" onClick={() => setAt(null)} aria-current={!at ? 'page' : undefined}>{t('wizard.allSectors')}</button>
        {crumbs.map((n) => (
          <React.Fragment key={n.id}>
            <span aria-hidden="true">›</span>
            <button type="button" onClick={() => setAt(n.id)} aria-current={n.id === at ? 'page' : undefined}>{nodeName(n, lang)}</button>
          </React.Fragment>
        ))}
      </nav>

      {current && canPick(tree, current) && (
        <div className="wz-use">
          <button type="button" className="btn btn-primary btn-sm" onClick={() => onPick(current.id)}>{t('wizard.useThis', { name: nodeName(current, lang) })}</button>
          <span className="muted small">{t('wizard.orDeeper')}</span>
        </div>
      )}

      {current && !canPick(tree, current) && (
        <div className="callout callout-info small wz-anyway">
          <p>{t('wizard.notTypeHere', { name: nodeName(current, lang) })}</p>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => onPick(current.id, { force: true })}>{t('wizard.putHereAnyway')}</button>
        </div>
      )}

      <div className="cat-grid">
        {level.map((n) => {
          const kids = kidsOf(tree, n).length;
          const ok = kids > 0 || canPick(tree, n);
          return <CategoryTile key={n.id} tree={tree} node={n} lang={lang} selected={n.id === Number(value)}
            sub={kids ? t('wizard.optionsInside', { n: kids }) : ok ? t('wizard.select') : t('wizard.nothingHere')} onClick={() => open(n)} />;
        })}
      </div>
      {error && <p className="field-error" role="alert">{error}</p>}
    </div>
  );
}
